import CloseIcon from '@mui/icons-material/Close';
import SelfImprovementOutlinedIcon from '@mui/icons-material/SelfImprovementOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded';
import Avatar from '@mui/material/Avatar';
import {
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import Sensual90 from '@mui/icons-material/HistoryToggleOff';
import techniqueChecker from '../utils/techniqueChecker';
import formatSchedule from '../utils/formatSchedule';
import AvailabilityButton from './AvailabilityButton';
import EmblaGallery from './EmblaGallery';
import FeedbackSlider from './FeedbackSlider';
import './HoverCard.css';

const SHIATSU_PLACEHOLDER_IMAGE =
  'https://clubdynasty.ca/wp-content/uploads/2023/01/Female_Silouhette-scaled.jpg';
const NEW_BADGE_IMAGE =
  'https://clubdynasty.ca/wp-content/uploads/2022/01/new-purple-animation.gif';
const FOOT_MASSAGE_IMAGE =
  'https://clubdynasty.ca/wp-content/uploads/2022/11/foot_massage.gif';
const LINGAM_IMAGE =
  'https://clubdynasty.ca/wp-content/uploads/2024/07/lingam_icon.gif';

const DetailCard = ({ label, value }) => (
  <div className="thumbnail-profile-detail-card">
    <span className="thumbnail-profile-detail-label">{label}</span>
    <span className="thumbnail-profile-detail-value">{value}</span>
  </div>
);

const getTechniqueFeature = (technique) => {
  if (technique === 'Shiatsu') {
    return {
      id: 'feature-technique-shiatsu',
      label: technique,
      icon: <SelfImprovementOutlinedIcon sx={{ fontSize: 18 }} />,
      avatarClassName: 'thumbnail-profile-chip-avatar--technique',
    };
  }

  return {
    id: `feature-technique-${String(technique).toLowerCase()}`,
    label: technique || 'Technique',
    icon: <SpaOutlinedIcon sx={{ fontSize: 18 }} />,
    avatarClassName: 'thumbnail-profile-chip-avatar--technique',
  };
};

const renderFeatureAvatar = (feature) => (
  <Avatar
    src={feature.imageSrc}
    alt={feature.label}
    className={`thumbnail-profile-chip-avatar ${
      feature.avatarClassName || ''
    }`.trim()}
    imgProps={{
      className: 'thumbnail-profile-chip-avatar-image',
    }}
  >
    {feature.icon || null}
  </Avatar>
);

const ThumbnailDetailsDialog = ({
  open,
  masseuse,
  onClose,
  slots,
  available_on,
}) => {
  const dialogContent = useMemo(() => {
    if (!masseuse) {
      return {
        detailItems: [],
        featureLabels: [],
        galleryImages: [],
        galleryThumbnails: [],
        heroSubtitle: '',
      };
    }

    const showExtendedProfile = techniqueChecker(masseuse.technique);
    const canShowGallery = showExtendedProfile || masseuse.name === 'Crystal';
    const galleryImages = canShowGallery
      ? [masseuse.image, ...(masseuse.images || [])].filter(Boolean)
      : [SHIATSU_PLACEHOLDER_IMAGE];
    const galleryThumbnails = canShowGallery
      ? [masseuse.image, ...(masseuse.newThumbnails || [])].filter(Boolean)
      : [SHIATSU_PLACEHOLDER_IMAGE];

    const detailItems = [
      { label: 'Technique', value: masseuse.technique || 'Not available' },
      ...(showExtendedProfile
        ? [{ label: 'Age', value: masseuse.age || 'Not available' }]
        : []),
      // { label: 'Background', value: masseuse.background || 'Not available' },
      { label: 'Language', value: masseuse.language || 'Not available' },
      ...(showExtendedProfile
        ? [
            {
              label: 'Clientele',
              value: masseuse.services || 'Not available',
            },
            {
              label: 'Tattoos',
              value: masseuse.tattoos ? 'Yes' : 'No',
            },
          ]
        : []),
      { label: 'Height', value: masseuse.height || 'Not available' },
      ...(showExtendedProfile
        ? [
            {
              label: 'Measurements',
              value: masseuse.measurements || 'Not available',
            },
            {
              label: 'Interests',
              value: masseuse.interests || 'Not available',
            },
            {
              label: 'Bust',
              value: masseuse.bust_enhanced ? 'Enhanced' : 'Natural',
            },
            {
              label: 'Hair Color',
              value: masseuse?.hair_color?.length
                ? masseuse.hair_color
                : 'Not available',
            },
          ]
        : []),
    ];

    const featureLabels = [
      // getTechniqueFeature(masseuse.technique),
      masseuse.new
        ? {
            id: 'feature-new',
            label: 'New',
            imageSrc: NEW_BADGE_IMAGE,
          }
        : null,
      masseuse.foot_massage
        ? {
            id: 'feature-foot-massage',
            label: 'Foot Massage',
            imageSrc: FOOT_MASSAGE_IMAGE,
          }
        : null,
      masseuse.allow_lingam
        ? {
            id: 'feature-lingam',
            label: 'Lingam Massage',
            imageSrc: LINGAM_IMAGE,
          }
        : null,
      masseuse.sensual90
        ? {
            id: 'feature-90-minutes',
            label: '90 Minutes',
            icon: (
              <Sensual90 sx={{ fontSize: 18, color: 'rgb(255, 140, 26)' }} />
            ),
            avatarClassName: 'thumbnail-profile-chip-avatar--duration',
          }
        : null,
      masseuse.vacation
        ? {
            id: 'feature-returning-soon',
            label: 'Returning Soon',
            icon: <UpdateRoundedIcon sx={{ fontSize: 18 }} />,
            avatarClassName: 'thumbnail-profile-chip-avatar--returning',
          }
        : null,
    ].filter(Boolean);

    const heroSubtitle = showExtendedProfile
      ? [masseuse.age, masseuse.background].filter(Boolean).join(' • ')
      : [masseuse.background, masseuse.technique].filter(Boolean).join(' • ');

    return {
      detailItems,
      featureLabels,
      galleryImages,
      galleryThumbnails,
      heroSubtitle,
    };
  }, [masseuse]);

  if (!masseuse) {
    return null;
  }

  const schedule = masseuse.schedule?.length
    ? masseuse.schedule.map((entry) => formatSchedule(entry))
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        className: 'thumbnail-profile-dialog-paper',
      }}
    >
      <IconButton
        aria-label="close profile"
        className="thumbnail-profile-dialog__close"
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent className="thumbnail-profile-dialog__content">
        {masseuse.vacation && (
          <div className="thumbnail-profile-banner">Returning Soon</div>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <div className="thumbnail-profile-summary thumbnail-profile-summary--mobile">
              <Typography className="thumbnail-profile-heading">
                {masseuse.name}
              </Typography>
              <Typography className="thumbnail-profile-summary-meta">
                {dialogContent.heroSubtitle || 'Profile overview'}
              </Typography>
              <div className="thumbnail-profile-badges thumbnail-profile-badges--summary">
                {dialogContent.featureLabels.map((feature) => (
                  <Chip
                    key={feature.id}
                    label={feature.label}
                    size="medium"
                    avatar={renderFeatureAvatar(feature)}
                    className="thumbnail-profile-chip"
                  />
                ))}
              </div>
            </div>
            <div className="thumbnail-profile-media-shell">
              <div className="thumbnail-profile-gallery-shell">
                <EmblaGallery
                  images={dialogContent.galleryImages}
                  thumbnails={dialogContent.galleryThumbnails}
                  height={360}
                  backgroundColor="#f7f1e7"
                  objectFit="contain"
                  onErrorImageURL={SHIATSU_PLACEHOLDER_IMAGE}
                />
              </div>

              <div className="thumbnail-profile-section">
                <Typography className="thumbnail-profile-section-title">
                  Bio
                </Typography>
                <Typography className="thumbnail-profile-bio">
                  {masseuse.bio || 'Not available'}
                </Typography>
              </div>
            </div>
            <div className="thumbnail-profile-availability">
              <AvailabilityButton
                masseuse={masseuse}
                slots={slots}
                available_on={available_on}
                buttonSize="medium"
                marginTop={0}
                marginBottom={0}
              />
            </div>
          </Grid>

          <Grid item xs={12} md={7}>
            <div className="thumbnail-profile-summary thumbnail-profile-summary--desktop">
              <Typography className="thumbnail-profile-heading">
                {masseuse.name}
              </Typography>
              <Typography className="thumbnail-profile-summary-meta">
                {dialogContent.heroSubtitle || 'Profile overview'}
              </Typography>
              <div className="thumbnail-profile-badges thumbnail-profile-badges--summary">
                {dialogContent.featureLabels.map((feature) => (
                  <Chip
                    key={feature.id}
                    label={feature.label}
                    size="medium"
                    avatar={renderFeatureAvatar(feature)}
                    className="thumbnail-profile-chip"
                  />
                ))}
              </div>
            </div>

            <div className="thumbnail-profile-section">
              <Typography className="thumbnail-profile-section-title">
                Profile Details
              </Typography>
              <div className="thumbnail-profile-detail-grid">
                {dialogContent.detailItems.map((item) => (
                  <DetailCard
                    key={item.label}
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </div>
            </div>
          </Grid>

          <Grid item xs={12}>
            <div className="thumbnail-profile-section">
              <Typography className="thumbnail-profile-section-title">
                Schedule
              </Typography>
              {schedule.length ? (
                <div className="thumbnail-profile-schedule-list">
                  {schedule.map((entry, index) => (
                    <div
                      className="thumbnail-profile-schedule-item"
                      key={`${masseuse.id}-schedule-${index}`}
                    >
                      {entry}
                    </div>
                  ))}
                </div>
              ) : (
                <Typography className="thumbnail-profile-empty-state">
                  Not available
                </Typography>
              )}
            </div>
          </Grid>

          <Grid item xs={12}>
            <div className="thumbnail-profile-section thumbnail-profile-feedback">
              <Typography className="thumbnail-profile-section-title">
                Customer Feedback
              </Typography>
              <FeedbackSlider masseuseId={masseuse.id} />
            </div>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ThumbnailDetailsDialog;
